import {
  BATCH_FEEDBACK_PATH,
  FEEDBACK_PATH,
  FIELD_PAYLOAD,
  FIELD_SCREENSHOT,
  screenshotField,
  type BatchFeedbackPayload,
  type FeedbackErrorBody,
  type FeedbackErrorCode,
  type FeedbackPayload,
} from "@previewloop/shared";

export type SubmitDeps = { fetch?: typeof fetch };

/** Submission failure carrying the HTTP status — and, when the API sent a structured
 * rejection body, its machine-readable code (plus the offending origin for
 * "origin_not_allowed" and the retry window for "rate_limited") — so callers can
 * react structurally. */
export class SubmitError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly code?: FeedbackErrorCode,
    readonly origin?: string,
    readonly retryAfter?: number,
  ) {
    super(message);
    this.name = "SubmitError";
  }
}

async function toSubmitError(res: Response, what: string): Promise<SubmitError> {
  let body: FeedbackErrorBody | undefined;
  try {
    body = (await res.json()) as FeedbackErrorBody;
  } catch {
    // non-JSON body (older API, proxy error page) — status-only error
  }
  return new SubmitError(
    res.status,
    `PreviewLoop: ${what} failed (${res.status})`,
    body?.error,
    body?.origin,
    body?.retryAfter,
  );
}

export async function submitBatchFeedback(
  apiOrigin: string,
  payload: BatchFeedbackPayload,
  screenshots: Blob[],
  deps: SubmitDeps = {},
): Promise<{ ids: string[] }> {
  const doFetch = deps.fetch ?? fetch;
  const origin = apiOrigin.replace(/\/$/, "");

  const body = new FormData();
  body.set(FIELD_PAYLOAD, JSON.stringify(payload));
  screenshots.forEach((shot, i) => body.set(screenshotField(i), shot, `screenshot_${i}.png`));

  const res = await doFetch(`${origin}${BATCH_FEEDBACK_PATH}`, { method: "POST", body });
  if (!res.ok) throw await toSubmitError(res, "batch submit");

  return (await res.json()) as { ids: string[] };
}

export async function submitFeedback(
  apiOrigin: string,
  payload: FeedbackPayload,
  screenshot: Blob,
  deps: SubmitDeps = {},
): Promise<{ id: string }> {
  const doFetch = deps.fetch ?? fetch;
  const origin = apiOrigin.replace(/\/$/, "");

  const body = new FormData();
  body.set(FIELD_PAYLOAD, JSON.stringify(payload));
  body.set(FIELD_SCREENSHOT, screenshot, "screenshot.png");

  const res = await doFetch(`${origin}${FEEDBACK_PATH}`, { method: "POST", body });
  if (!res.ok) throw await toSubmitError(res, "feedback submit");

  return (await res.json()) as { id: string };
}
